/**
 * @graphql agg
 */

import { useCallback } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type {
	GraphQLContentStatus,
	useAllCommentsDataQuery$data,
	useAllCommentsDataQuery$variables,
} from './__generated__/useAllCommentsDataQuery.graphql.ts';
import {
	findCommentInData,
	findRootCommentForReply,
	OPTIMISTIC_SAVE_COMMENT_ID,
} from './commentDataManagerUtils.ts';

// Extracted types:
type Comments = NonNullable<useAllCommentsDataQuery$data['comments']>;
type QueryComment = NonNullable<NonNullable<Comments>['nodes']>[number];
export type Comment = NonNullable<QueryComment>;
export type CommentAuthor = NonNullable<Comment>['author'];
export type CommentLocation = NonNullable<Comment>['location'];
export type CommentReactionsSummary = NonNullable<Comment>['reactionsSummary'];
export type CommentReplies = NonNullable<Comment>['replies'];
export type CommentReply = NonNullable<NonNullable<CommentReplies>[number]>;
export type CommentContributors = NonNullable<Comment>['contributors'];

// Narrowed views of a comment's location, derived from the live comments-data-manager query.
export type CommentFooterLocation = Pick<
	NonNullable<CommentLocation>,
	'type' | 'commentResolveProperties'
>;
export type FooterResolvedProperties =
	NonNullable<CommentFooterLocation>['commentResolveProperties'];
export type CommentInlineLocation = Pick<
	NonNullable<CommentLocation>,
	'type' | 'inlineMarkerRef' | 'inlineText' | 'inlineResolveProperties'
>;
export type CommentInlineResolveProperties = NonNullable<
	CommentInlineLocation['inlineResolveProperties']
>;

type Modifiable<T> = { -readonly [P in keyof T]: T[P] };
type ModifiableCommentReplies = Modifiable<CommentReplies>;

// Constants
// By default comments queries only return 250 comments.
// This ensures that we get everything, without opening ourselves up too much to performance issues.
export const MAX_COMMENTS_TO_RETURN = 1500;

// All possible content statuses for queries that need to fetch all comments regardless of status
export const allContentStatuses: GraphQLContentStatus[] = [
	'DRAFT',
	'CURRENT',
	'ARCHIVED',
	'DELETED',
];

// A hook to load all comments data for a page, with categorization and utility functions. This
// allows components to easily access and manage comments data, and also allows us to more easily
// manage updates to local cache in a single place vs. having "many sources of truth" for comments
// data that we had to try to keep in sync.  The downside is that this hook loads all comments data
// for a page, so it may be slower on pages with a large number of comments.

// Data Query Definitions:

// Unfortunately our Relay-friendly type generation tools don't support using GQL fragments yet -
// but in order to keep the definition of the comment fields in sync (so the cache update fragment
// is always in sync with the full query) we will need to manually make any changes to one
// definition in any others as well, until our tooling supports fragments and then we move to a
// more fragmented-based approach.  Note there are also comment queries or fragments in the
// following files that need to be kept in sync as well:
// - next/packages/comments-data/src/hooks/useCommentById.ts
// - next/packages/comments-data-manager/src/updateCommentInCache.ts
// - next/packages/comments-data-manager/src/useCreateObjectCommentMutation.ts
// - next/packages/comments-data-manager/src/useCreateInlineCommentMutation.ts
// - next/packages/comments-data-manager/src/useCreateInlineReplyMutation.ts
// - next/packages/object-comments/src/components/ObjectCommentContainer.tsx

// Fragment for reusable comment data structure, used in main query and cache updates
// TODO: Uncommment this fragment when our tooling supports fragments being spread into queries.
// Note that if we are not allowed to share fragments between files/packages, we should move the
// parts of those other hooks/functions that use this fragment into this file.
// const useAllCommentsData_commentDataFragment = graphql`
// 	fragment useAllCommentsData_commentDataFragment on Comment {
// 		id
// 		author {
//			__typename
// 			displayName
// 			permissionType
// 			... on KnownUser {
// 				accountId
// 				permissionType
// 			}
// 			profilePicture {
// 				path
// 			}
// 			type
// 		}
// 		body(representation: ATLAS_DOC_FORMAT) {
// 			value
// 		}
// 		container {
// 			id
// 		}
// 		contentStatus
// 		createdAt {
// 			value
// 		}
// 		createdAtNonLocalized
// 		links {
// 			editui
// 			webui
// 		}
// 		location {
//			__typename
// 			type
// 			... on FooterComment {
// 				commentResolveProperties {
// 					resolved
// 					resolvedFriendlyDate
// 					resolvedTime
// 					resolvedUser
// 				}
// 			}
// 			... on InlineComment {
// 				inlineMarkerRef
// 				inlineResolveProperties {
// 					isDangling
// 					resolved
// 					resolvedFriendlyDate
// 					resolvedTime
// 					resolvedUser
// 				}
// 				inlineText
// 			}
// 		}
// 		parentId
// 		permissions {
// 			isEditable
// 			isRemovable
// 			isResolvable
// 		}
// 		reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
// 			ari
// 			containerAri
// 			reactionsCount
// 			reactionsSummaryForEmoji {
// 				id
// 				count
// 				emojiId
// 				reacted
// 			}
// 		}
// 		replies {
// 			id
// 			author {
//				__typename
// 				displayName
// 				permissionType
// 				... on KnownUser {
// 					accountId
// 				}
// 				profilePicture {
// 					path
// 				}
// 			}
// 			body(representation: ATLAS_DOC_FORMAT) {
// 				value
// 			}
// 			contentStatus
// 			createdAt {
// 				value
// 			}
// 			createdAtNonLocalized
// 			links {
// 				editui
// 				webui
// 			}
// 			location {
//				__typename
// 				type
// 				... on FooterComment {
// 					commentResolveProperties {
// 						resolved
// 						resolvedFriendlyDate
// 						resolvedTime
// 						resolvedUser
// 					}
// 				}
// 				... on InlineComment {
// 					inlineMarkerRef
// 					inlineResolveProperties {
// 						isDangling
// 						resolved
// 						resolvedFriendlyDate
// 						resolvedTime
// 						resolvedUser
// 					}
// 					inlineText
// 				}
// 			}
// 			parentId
// 			permissions {
// 				isEditable
// 				isRemovable
// 				isResolvable
// 			}
// 			reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
// 				ari
// 				containerAri
// 				reactionsCount
// 				reactionsSummaryForEmoji {
// 					id
// 					count
// 					emojiId
// 					reacted
// 				}
// 			}
// 			version {
// 				by {
//					__typename
// 					displayName
// 				}
// 				number
// 				when
// 			}
// 		}
// 		version {
// 			by {
//				__typename
// 				type
// 				displayName
// 				... on KnownUser {
// 					accountId
// 				}
// 			}
// 			friendlyWhen
// 			number
// 			when
// 		}
// 	}
// `;

// Main GraphQL query with fragment spread
// TODO: Replace with fragment when our tooling supports it
export const allCommentsDataQuery: GraphQLTaggedNode = graphql`
	query useAllCommentsDataQuery(
		$pageId: ID!
		$contentStatus: [GraphQLContentStatus]
		$first: Long!
	) {
		comments(pageId: $pageId, contentStatus: $contentStatus, depth: ROOT, first: $first) {
			nodes {
				id
				author {
					__typename
					displayName
					permissionType
					... on KnownUser {
						accountId
						permissionType
					}
					profilePicture {
						path
					}
					type
				}
				contributors {
					__typename
					displayName
					profilePicture {
						path
					}
					... on KnownUser {
						accountId
					}
				}
				body(representation: ATLAS_DOC_FORMAT) {
					value
				}
				container {
					id
				}
				contentStatus
				createdAt {
					value
				}
				createdAtNonLocalized
				links {
					editui
					webui
				}
				location {
					__typename
					type
					... on FooterComment {
						commentResolveProperties {
							resolved
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
					}
					... on InlineComment {
						inlineMarkerRef
						inlineResolveProperties {
							isDangling
							resolved
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
						inlineText
					}
				}
				parentId
				permissions {
					isEditable
					isRemovable
					isResolvable
				}
				reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
					ari
					containerAri
					reactionsCount
					reactionsSummaryForEmoji {
						id
						count
						emojiId
						reacted
					}
				}
				replies {
					id
					author {
						__typename
						displayName
						permissionType
						... on KnownUser {
							accountId
						}
						profilePicture {
							path
						}
					}
					contributors {
						__typename
						displayName
						profilePicture {
							path
						}
						... on KnownUser {
							accountId
						}
					}
					body(representation: ATLAS_DOC_FORMAT) {
						value
					}
					contentStatus
					createdAt {
						value
					}
					createdAtNonLocalized
					links {
						editui
						webui
					}
					location {
						__typename
						type
						... on FooterComment {
							commentResolveProperties {
								resolved
								resolvedFriendlyDate
								resolvedTime
								resolvedUser
							}
						}
						... on InlineComment {
							inlineMarkerRef
							inlineResolveProperties {
								isDangling
								resolved
								resolvedFriendlyDate
								resolvedTime
								resolvedUser
							}
							inlineText
						}
					}
					parentId
					permissions {
						isEditable
						isRemovable
						isResolvable
					}
					reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
						ari
						containerAri
						reactionsCount
						reactionsSummaryForEmoji {
							id
							count
							emojiId
							reacted
						}
					}
					version {
						by {
							__typename
							displayName
						}
						number
						when
					}
				}
				version {
					by {
						__typename
						type
						displayName
						... on KnownUser {
							accountId
						}
					}
					friendlyWhen
					number
					when
				}
			}
			totalCount
			__typename
		}
	}
`;

/**
 * Builds consistent query variables for fetching comments data.
 * This ensures we always use the same variable structure across the app.
 *
 * @param contentId - The ID of the content to get comments for
 * @returns Query variables object with pageId and contentStatus
 */
export const buildUseAllCommentsDataQueryVariables = (
	contentId: string,
): useAllCommentsDataQuery$variables => ({
	pageId: contentId,
	contentStatus: allContentStatuses,
	first: MAX_COMMENTS_TO_RETURN,
});

// Types moved to commentDataManagerUtils.ts

type UpdateCommentsCacheResult = {
	updatedRootComments: Comment[];
	updatedTotalCount: number;
	error?: Error;
};

// Utilities for deleting comments from cache:

type AddRootCommentToCacheProps = {
	cachedRootComments: Comment[];
	totalCount: number;
	newComment: Comment;
	repliesCount: number;
};

const addCommentRootToCache = ({
	cachedRootComments,
	totalCount,
	newComment,
	repliesCount,
}: AddRootCommentToCacheProps): UpdateCommentsCacheResult => {
	const updatedRootComments = [...cachedRootComments];
	let updatedTotalCount = totalCount;

	// If there is already a comment with the same ID in the root array, replace it
	// Normally this shouldn't happen because new comments should always have unique IDs, but there
	// is a case were this can happen with replies (see below) - so let's be safe and handle it just
	// in case it ever happens with root comments as well.
	const existingRootIndex = updatedRootComments.findIndex(
		(comment) => newComment?.id && comment?.id === newComment?.id,
	);
	if (existingRootIndex !== -1) {
		updatedRootComments[existingRootIndex] = newComment;

		// No need to change total count
	} else {
		// Else add the newly added comment to the end of the root comments list
		updatedRootComments.push(newComment);

		// Increment total count. Note that we can only add individual comments without any replies, but
		// let's be safe and adjust for any replies in case this ever changes
		updatedTotalCount += repliesCount + 1;
	}

	return {
		updatedRootComments,
		updatedTotalCount,
		error: undefined,
	};
};

const addCommentReplyInCache = ({
	cachedRootComments,
	totalCount,
	newComment,
	repliesCount,
	parentId,
}: AddRootCommentToCacheProps & { parentId: string }): UpdateCommentsCacheResult => {
	// For replies, we need to add the reply from the top-level parent's replies list in the main
	// query's cache. This is because the reply arrays are actually flat arrays of Comment objects -
	// nested comments are not actually nested in the GQL data schema but stored flat in the root
	// parent's replies array with the parentId field that indicates which comment they really reply
	// to (we build the actual nested structure elsewhere in the FE). So to add a reply, we need to
	// find the top-level parent comment first.
	let updatedTotalCount = totalCount;
	let error: Error | undefined = undefined;

	// First, find the top-level parent comment
	const parentComment = findRootCommentForReply(cachedRootComments, parentId);
	if (parentComment) {
		// Create a copy of the parent's replies array, as the original is readonly
		const updatedReplies = [...parentComment.replies];

		// If there is already a reply with the same ID in the parent's replies, replace it
		// Normally this shouldn't happen because new comments should always have unique IDs, but there
		// is a case were this can happen - when we're in edit mode and another user who is also in
		// edit mode also adds a reply to comment on the new text and then Updates the page -> we get
		// the new reply via the pubsub events and again when the new text is added by the Update.
		const existingReplyIndex = updatedReplies.findIndex(
			(reply) => newComment?.id && reply?.id === newComment?.id,
		);
		if (existingReplyIndex !== -1) {
			updatedReplies[existingReplyIndex] = newComment;
			// Note there is no need to to change the total count
		} else {
			// Else add the new  reply to the array
			updatedReplies.push(newComment);
			// Increment total count. We can only add individual comments without any replies, but let's be
			// safe and adjust for any replies in case this ever changes
			updatedTotalCount += 1 + repliesCount;
		}

		// Coerce the readonly replies array to be modifiable so we don't have to create new
		// modifiable comment objects all the way up the parent chain
		(parentComment.replies as ModifiableCommentReplies) = updatedReplies;
	} else {
		// Parent comment not found
		error = Error(
			`Cannot add reply comment in cache: can not find parent comment with ID ${parentId}`,
		);
	}

	return {
		updatedRootComments: cachedRootComments,
		updatedTotalCount,
		error,
	};
};

const addCommentToCache = ({
	cachedRootComments,
	newComment,
	totalCount,
}: {
	cachedRootComments: Comment[];
	newComment: Comment;
	totalCount: number;
}): UpdateCommentsCacheResult => {
	// Update the appropriate part of the cache depending on whether it's a root comment or a reply
	const parentId = newComment?.parentId;
	const repliesCount = newComment?.replies?.length || 0;
	return !parentId
		? addCommentRootToCache({
				cachedRootComments,
				totalCount,
				newComment,
				repliesCount,
			})
		: addCommentReplyInCache({
				cachedRootComments,
				totalCount,
				newComment,
				repliesCount,
				parentId,
			});
};

// Utilities for deleting comments from cache:

type DeleteCommentUtilsProps = {
	cachedRootComments: Comment[];
	totalCount: number;
	commentId: string;
	repliesCount: number;
};

const deleteRootCommentInCache = ({
	cachedRootComments,
	totalCount,
	commentId,
	repliesCount,
}: DeleteCommentUtilsProps): UpdateCommentsCacheResult => {
	let updatedTotalCount = totalCount;

	// Remove the newly resolved comment from the list if we can find it
	// Create a copy of the root comment array without the deleted reply, as the original is readonly
	const updatedRootComments = cachedRootComments.filter((comment) => comment?.id !== commentId);
	// Decrement total count - currently we can't delete a reply that has any comments, but
	// let's be safe and adjust for any replies in case this ever changes
	updatedTotalCount -= 1 + repliesCount;

	return {
		updatedRootComments,
		updatedTotalCount,
		error: undefined,
	};
};

const deleteCommentReplyInCache = ({
	cachedRootComments,
	totalCount,
	commentId,
	repliesCount,
	parentId,
}: DeleteCommentUtilsProps & { parentId: string }): UpdateCommentsCacheResult => {
	// For replies, we need to remove the reply from the top-level comment's replies list in the main
	// query's cache. This is because the reply arrays are actually flat arrays of Comment objects -
	// nested comments are not actually nested in the GQL data schema but stored flat in the root
	// parent's replies array with the parentId field that indicates which comment they really reply
	// to (we build the actual nested structure elsewhere in the FE).  So to remove a reply, we need to
	// find the top-level parent comment first.
	let updatedTotalCount = totalCount;
	let error: Error | undefined = undefined;

	// First, find the top-level parent comment
	const parentComment = findRootCommentForReply(cachedRootComments, parentId);
	if (parentComment) {
		// Create a copy of the parent's replies array without the deleted reply, as the original
		// is readonly
		const updatedReplies = (parentComment.replies || []).filter((reply) => reply?.id !== commentId);
		// Coerce the readonly replies array to be modifiable so we don't have to create new
		// modifiable comment objects all the way up the parent chain
		(parentComment.replies as ModifiableCommentReplies) = updatedReplies;

		// Decrement total count. Currently we can't delete a reply that has any comments. Also
		// since we can’t have spread fragments in Relay-friendly queries yet, the current
		// CommentReply type doesn't have a `replies` field. And the backend, sends the nested
		// replies to us in a flat list with the correct parentIDs so the FE must manually nest
		// them. But let's adjust the count for replies just in case this ever changes.
		updatedTotalCount -= 1 + repliesCount;
	} else {
		// Parent comment not found
		error = Error(
			`Cannot delete reply comment in cache: can not find parent comment with ID ${parentId}`,
		);
	}

	return {
		updatedRootComments: cachedRootComments,
		updatedTotalCount,
		error,
	};
};

const removeCommentFromCache = ({
	cachedRootComments,
	commentId,
	totalCount,
}: {
	cachedRootComments: Comment[];
	commentId: string;
	totalCount: number;
}): UpdateCommentsCacheResult => {
	// Find the comment to delete to determine if it's a root comment or a reply
	const commentToDelete = findCommentInData(cachedRootComments, commentId);
	if (!commentToDelete) {
		return {
			updatedRootComments: [],
			updatedTotalCount: 0,
			error: new Error(`Cannot delete comment in cache: comment with ID ${commentId} not found`),
		};
	}

	// Update the appropriate part of the cache depending on whether it's a root comment or a reply
	const parentId = commentToDelete.parentId;
	const repliesCount = commentToDelete.replies?.length || 0;
	return !parentId
		? deleteRootCommentInCache({
				cachedRootComments,
				totalCount,
				commentId,
				repliesCount,
			})
		: deleteCommentReplyInCache({
				cachedRootComments,
				totalCount,
				commentId,
				repliesCount,
				parentId,
			});
};

export type FetchPolicies =
	| 'cache-first'
	| 'cache-only'
	| 'cache-and-network'
	| 'network-only'
	| 'no-cache';

export type UseAllCommentsDataOptions = {
	fetchPolicy?: FetchPolicies;
	onCompleted?: (data: useAllCommentsDataQuery$data) => void;
	onError?: (error: Error) => void;
	skip?: boolean;
};

export type RefetchOptions = {
	onError?: (error: Error) => void;
	preserveProcessedData?: boolean;
};

export type UseAllCommentsDataProps = {
	contentId: string;
	options?: UseAllCommentsDataOptions;
};

export type AddCommentInCacheProps = {
	newComment: Comment;
	onError?: (error: Error) => void;
	onCacheMiss?: (error: Error) => void;
};

type DeleteCommentInCacheProps = {
	commentId: string;
	onError?: (error: Error) => void;
	onCacheMiss?: (error: Error) => void;
};

export type UseAllCommentsDataReturn = {
	loading: boolean;
	error: Error | undefined;
	refetch: (options?: RefetchOptions) => Promise<any>;
	data: useAllCommentsDataQuery$data | undefined;
	addCommentInCache: ({ newComment, onError, onCacheMiss }: AddCommentInCacheProps) => void;
	deleteCommentInCache: ({ commentId, onError, onCacheMiss }: DeleteCommentInCacheProps) => boolean;
};

/**
 * Custom React hook for loading all GraphQL comments data for a page
 *
 * Features:
 * - Loads all comments (inline and footer) for the current page via GraphQL
 * - Handles loading states and errors gracefully
 * - Supports various fetch policies and options
 * - Provides cache manipulation utilities (addCommentInCache, deleteCommentInCache)
 * - Includes comprehensive type safety
 *
 * Note: This hook no longer processes/categorizes comments. Raw comment data is returned
 * and processing is handled by CommentsDataBackgroundLoader which updates the
 * ProcessedCommentsDataStore. Consumers should use useProcessedCommentsDataState
 * to access categorized/filtered comments.
 *
 * @param options - Configuration options for the hook
 * @returns Object containing raw comments data, loading state, and cache utilities
 */
export const useAllCommentsData = ({
	contentId,
	options = {},
}: UseAllCommentsDataProps): UseAllCommentsDataReturn => {
	const {
		fetchPolicy = 'cache-first',
		onCompleted: parentOnCompleted,
		onError: parentOnError,
	} = options;

	// Shared handling for query errors (initial load and refetch): mark certain errors as handled, then invoke callback.
	// WS-3494 / COMMENTS-188
	const applyQueryErrorHandling = useCallback((error: Error, callback?: (error: Error) => void) => {
		if (error?.message?.search(/reactions/i) !== -1 || error?.message?.search(/cc-pages/i) !== -1) {
			markErrorAsHandled(error);
		}
		callback?.(error);
	}, []);

	const handleQueryError = useCallback(
		(error: Error) => applyQueryErrorHandling(error, parentOnError),
		[applyQueryErrorHandling, parentOnError],
	);

	// Skip query if no content ID or explicitly skipped
	const shouldSkip = !contentId || !!options.skip;

	const queryResponse = useQueryInterop<
		useAllCommentsDataQuery$data,
		useAllCommentsDataQuery$variables
	>(allCommentsDataQuery, {
		variables: buildUseAllCommentsDataQueryVariables(contentId ?? ''),
		skip: shouldSkip,
		context: { single: true }, // We auto-batch queries that fire within 10ms of eachother. Since this query is slow, this unbatches it to avoid slowing down everything else
		fetchPolicy,
		onCompleted: (data: useAllCommentsDataQuery$data) => {
			parentOnCompleted?.(data);
		},
		onError: handleQueryError,
	});

	const refetch = useCallback(
		async ({ onError: refetchOnError }: RefetchOptions = {}) => {
			try {
				return await queryResponse.refetch();
			} catch (error) {
				applyQueryErrorHandling(error as Error, refetchOnError ?? parentOnError);
			}
		},
		[queryResponse, applyQueryErrorHandling, parentOnError],
	);

	const addCommentInCache = useCallback(
		({ newComment, onError, onCacheMiss }: AddCommentInCacheProps) => {
			// If the new comment is a root comment, we need to add it to the root comments list and
			// update the main query's cache and totalCount. If it's a reply, we need to add it to its
			// parent comments's replies list in the cache - but we still  update the whole main query
			// (vs. just update the parent comment itself via a fragment update) because we need to
			// update the main query's totalCount in the cache to keep everything in sync.
			try {
				// Read the (root level) query from the cache
				let dataProxy: useAllCommentsDataQuery$data | null = null;
				try {
					dataProxy = readQueryInterop<
						useAllCommentsDataQuery$data,
						useAllCommentsDataQuery$variables
					>({
						query: allCommentsDataQuery,
						variables: buildUseAllCommentsDataQueryVariables(contentId),
					});
				} catch (e) {
					// If cache miss, readQuery throws an error, which we ignore if comment is optimisticResponse.
					// Otherwise rethrow to invoke `onCacheMiss`.
					if (expValEquals('confluence_fe_handle_use_all_comments_cache_miss', 'isEnabled', true)) {
						if (newComment?.id !== OPTIMISTIC_SAVE_COMMENT_ID) {
							onCacheMiss?.(
								new Error(
									`Cache miss when adding comment with ID ${newComment?.id} to cache: ${e}`,
								),
							);
						}
						return;
					}
					throw e;
				}

				// Make a shallow copy that's not readonly if there are nodes (which are readonly),
				// otherwise use an empty array
				const nodes: QueryComment[] = dataProxy?.comments?.nodes
					? [...dataProxy.comments.nodes]
					: [];
				const cachedRootComments: Comment[] = nodes.filter(
					(comment): comment is Comment => !!comment,
				);
				const totalCount = dataProxy?.comments?.totalCount || 0;

				const { updatedRootComments, updatedTotalCount, error } = addCommentToCache({
					cachedRootComments,
					newComment,
					totalCount,
				});

				if (error) {
					onError?.(error);
					return;
				}

				writeQueryInterop<useAllCommentsDataQuery$data, useAllCommentsDataQuery$variables>({
					query: allCommentsDataQuery,
					variables: buildUseAllCommentsDataQueryVariables(contentId),
					data: {
						comments: {
							nodes: updatedRootComments,
							totalCount: updatedTotalCount,
							__typename: 'PaginatedCommentList',
						},
					},
				});
			} catch (err) {
				onError?.(
					new Error(
						`An error occurred when adding new comment to cache with ID ${newComment?.id}: ${err}`,
					),
				);
			}
		},
		[contentId],
	);

	const deleteCommentInCache = useCallback(
		({ commentId, onError, onCacheMiss }: DeleteCommentInCacheProps): boolean => {
			// First, we try to find the comment to determine if it's a root comment or a reply.  If it's a
			// root comment, we need to remove it from the root comments list and update the main query's
			// cache and totalCount. If it's a reply, we need to update its parent comments's replies list
			// in the cache - but we still  update the whole main query (vs. just update the parent comment
			// itself via a fragment update) because we need to update the main query's totalCount in the
			// cache to keep everything in sync.
			try {
				// Read the (root level) query from the cache
				let dataProxy: useAllCommentsDataQuery$data | null = null;
				try {
					dataProxy = readQueryInterop<
						useAllCommentsDataQuery$data,
						useAllCommentsDataQuery$variables
					>({
						query: allCommentsDataQuery,
						variables: buildUseAllCommentsDataQueryVariables(contentId),
					});
				} catch (e) {
					if (expValEquals('confluence_fe_handle_use_all_comments_cache_miss', 'isEnabled', true)) {
						onCacheMiss?.(
							new Error(`Cache miss when deleting comment with ID ${commentId} from cache: ${e}`),
						);
						return false;
					}
					throw e;
				}

				// Make a shallow copy that's not readonly if there are nodes (which are readonly),
				// otherwise use an empty array
				const nodes: QueryComment[] = dataProxy?.comments?.nodes
					? [...dataProxy.comments.nodes]
					: [];
				const cachedRootComments: Comment[] = nodes.filter(
					(comment): comment is Comment => !!comment,
				);
				const totalCount = dataProxy?.comments?.totalCount || 0;

				const { updatedRootComments, updatedTotalCount, error } = removeCommentFromCache({
					cachedRootComments,
					commentId,
					totalCount,
				});

				if (error) {
					onError?.(error);
					return false;
				}

				writeQueryInterop<useAllCommentsDataQuery$data, useAllCommentsDataQuery$variables>({
					query: allCommentsDataQuery,
					variables: buildUseAllCommentsDataQueryVariables(contentId),
					data: {
						comments: {
							nodes: updatedRootComments,
							totalCount: updatedTotalCount,
							__typename: 'PaginatedCommentList',
						},
					},
				});

				return true;
			} catch (err) {
				onError?.(
					new Error(`An error occurred when deleting cached comment with ID ${commentId}: ${err}`),
				);
				return false;
			}
		},
		[contentId],
	);

	return {
		loading: queryResponse.loading,
		error: queryResponse.error,
		refetch,
		data: queryResponse.data,
		addCommentInCache,
		deleteCommentInCache,
	};
};

// Read the full comments query data from the cache. This function can be used for getting updated
// comment data outside of React components.
// Note for Apollo GQL this will return the same data reference as the main query's data if the
// cache is unchanged, so this is safe to use for cache reads and comparisons without worrying
// about stale data references.
// TODO: Verify the data reference behavior for Relay GQL when we start enabling Relay support.
export const getCommentsDataFromCache = (
	contentId: string,
): useAllCommentsDataQuery$data | null => {
	const dataProxy: useAllCommentsDataQuery$data | null = readQueryInterop<
		useAllCommentsDataQuery$data,
		useAllCommentsDataQuery$variables
	>({
		query: allCommentsDataQuery,
		variables: buildUseAllCommentsDataQueryVariables(contentId),
	});

	return dataProxy;
};
